import {
  type ExoticPrice,
  exoticFabricationTotal,
  type StatCell,
  type Unit,
} from "@sins2-atlas/game-data";
import { getRouteApi, Link } from "@tanstack/react-router";
import { clsx } from "clsx";
import { Fragment, type ReactNode, useState } from "react";
import { z } from "zod";
import { AbilityEffects, AbilityLevelMark } from "../components/ability-effects";
import { ArtImage } from "../components/art-image";
import { Card } from "../components/card";
import { CompareTray } from "../components/compare-tray";
import { CorruptionCard } from "../components/corruption-card";
import {
  AbilityPointsDropdown,
  CorruptionDropdown,
  LevelDropdown,
  LoadoutDropdown,
  TechDropdown,
} from "../components/frame-toggle";
import { ProvidesValue } from "../components/provides-line";
import { StatCellView } from "../components/stat-cell";
import { StatesLegend } from "../components/states-legend";
import { UnitFacetChips } from "../components/unit-chips";
import { UnitItemsSection } from "../components/unit-items";
import {
  AbilityPointsNotice,
  CompareParamNotice,
  CorruptionNotice,
  CorruptionPoolNotice,
  LevelNotice,
  LoadoutNotice,
  LoadoutSettledNotice,
  TechNotice,
} from "../components/url-notice";
import { WeaponTable } from "../components/weapon-table";
import {
  clampCompare,
  compareToggle,
  parseCompareParam,
  serializeCompareParam,
} from "../lib/compare";
import { dataset, exoticName, exoticsById, unitsById } from "../lib/dataset";
import {
  craftCountText,
  disclosureById,
  exoticComponentNote,
  SQUADRONS_NOTE,
  VIRTUAL_SUPPLY_NOTE,
} from "../lib/disclosures";
import { factionLinkSearch } from "../lib/faction-selection";
import { exactNumber } from "../lib/format";
import {
  parseFrame,
  type StatFrame,
  serializeFrame,
  serializeLevelSearch,
  serializeLoadoutSearch,
  serializePointsSearch,
} from "../lib/frame";
import { abilityLevel, frameCell, frameExoticPrice, frameSquadrons } from "../lib/frame-fold";
import { type FrameTransition, reconcileFrame } from "../lib/frame-reconcile";
import {
  equipOnUnit,
  hasItemSlots,
  type LoadoutTransition,
  settleLoadout,
  unequipAllOnUnit,
  unequipOnUnit,
} from "../lib/loadout-index";
import {
  isLineGroup,
  type LineRow,
  lineRows,
  PROVIDES_NOTE,
  rowLabel,
  type StatGroupSpec,
  type StatRowSpec,
  sheetRows,
  sheetShowsHeading,
  statModelFor,
  viewKindOf,
} from "../lib/stat-model";
import { useDocumentTitle } from "../lib/title";
import { unitLinkSearch } from "../lib/unit-link";
import { SHIPS_VIEW, viewOf } from "../lib/views";
import { leverSearch, useFactionLink } from "./lever-search";

// Entity pages live at /unit/<id> — the id is the game's own stable entity id (ADR 0002:
// URLs are a public API; display names can change with a patch, ids far less so).
// `compare` is the pending comparison set (lib/compare.ts), following unit-level links.
export const unitSearchSchema = z.object({
  // The Faction Selection, the stat frame's two keys, and the two per-hull levers — declared
  // once for this route and /compare (routes/lever-search.ts, ADR 0012).
  ...leverSearch,
  compare: z.string().optional().catch(undefined),
});

const route = getRouteApi("/unit/$unitId");

/** The stat sheet's one row shell: label dt (helper-underlined when it carries a tooltip) +
 * mono dd. Every row in every card renders through this — five hand-rolled copies had
 * already drifted apart in padding and alignment before it was shared. */
function SheetRow({
  label,
  value,
  title,
  center = false,
  dense = false,
}: {
  label: ReactNode;
  value: ReactNode;
  title?: string;
  center?: boolean;
  /** Tighter, and without the top rule: for a sub-block that already sits under its own
   * separator (the Cost card's fabricated totals) and must not read as another stat row. */
  dense?: boolean;
}) {
  return (
    <div
      className={clsx(
        "flex justify-between gap-4",
        dense ? "py-1" : "border-t border-line-faint py-1.5 first:border-t-0",
        center ? "items-center" : "items-baseline",
      )}
    >
      <dt
        className={clsx(
          "font-mono text-[11px] uppercase tracking-[0.08em] text-ink-3",
          title && "cursor-help underline decoration-muted decoration-dotted underline-offset-2",
        )}
        title={title}
      >
        {label}
      </dt>
      <dd className="font-mono text-[14px] [font-variant-numeric:tabular-nums]">{value}</dd>
    </div>
  );
}

/** A stat cell row: the label carries the column's formula disclosure when one exists. */
function StatRow({
  id,
  label,
  cell,
  format,
}: {
  id?: string;
  label: string;
  cell: StatCell;
  format?: (value: number) => string;
}) {
  const disclosure = id === undefined ? undefined : disclosureById.get(id);
  return (
    <SheetRow
      label={label}
      title={disclosure?.formula}
      value={<StatCellView cell={cell} format={format} />}
    />
  );
}

/** One model row on this sheet. Order, label, cell and formatting are STAT_MODEL's; this
 * page only decides that a row is a `<dt>/<dd>` pair inside a card. The label is the
 * game's own for this unit where the model varies it (the Orbit row's per-kind label). */
function ModelRow({ spec, unit, frame }: { spec: StatRowSpec; unit: Unit; frame: StatFrame }) {
  return (
    <StatRow
      id={spec.id}
      label={rowLabel(spec, [unit])}
      cell={spec.cell(unit, frame)}
      {...(spec.format === undefined ? {} : { format: spec.format })}
    />
  );
}

/** One Provides line on this sheet (Issue 30): the game's own header as the label, the
 * composed names as the value, right-aligned so a six-kind list wraps against the label. */
function ProvidesRow({ row, unit, frame }: { row: LineRow; unit: Unit; frame: StatFrame }) {
  return (
    <SheetRow
      label={row.label}
      title={PROVIDES_NOTE}
      value={
        <span className="block text-right">
          <ProvidesValue line={row.lineOf(unit, frame)} />
        </span>
      }
    />
  );
}

/** One card of the stat sheet. The title is not PRINTED on a one-row group whose row already
 * carries the group's own name (the model's sheetShowsHeading rule, Issue 56): printing it
 * would read the same word twice, as the card's heading and again beside the value.
 *
 * It is still the card's name. `title` is required and always reaches `aria-label`, so the
 * card a reader cannot see a heading on is one a screen reader still hears named — a card
 * that dropped both was the shape this took at first (found in review, 2026-09-07). Keeping
 * the two as separate props rather than one optional title is what makes a nameless section
 * fail to compile instead of merely being wrong. */
function StatGroup({
  title,
  showHeading = true,
  bare = false,
  joined = false,
  children,
}: {
  title: string;
  showHeading?: boolean;
  /** Without the card chrome, for a group that shares a card with the one above it: the
   * Provides lines continue the Survivability card's pool rows (2026-09-07), so the border,
   * the surface and the padding belong to the CARD and this section only brings its rows.
   * Its rows carry `first:border-t-0`, so the join is seamless — which is how the game
   * prints these lines, straight on from the pools with nothing announcing them. */
  bare?: boolean;
  /** Draws the row rule at the join, for a bare section whose first row is a PEER of the rows
   * above it rather than a continuation of them. `first:border-t-0` is scoped to each
   * section's own `<dl>`, so a bare section's first row loses the rule every other row has and
   * reads as part of the row above — which is exactly right for the Provides lines (the game
   * prints them straight on from the pools) and exactly wrong for Squadrons, which stands over
   * the craft rows beneath it and was pairing upward with Speed instead (found in review
   * 2026-09-07, measured on the served page: every Combat row had a 1px rule and Squadrons had
   * none). On the section rather than the row, which lands in the same place: a normal row is
   * a rule then `py-1.5`, and so is this. */
  joined?: boolean;
  children: ReactNode;
}) {
  return (
    <section
      aria-label={title}
      className={clsx(
        !bare && "rounded-lg border border-line bg-surface px-4 py-3",
        bare && joined && "border-t border-line-faint",
      )}
    >
      {showHeading && (
        <h2 className="mb-2 font-condensed text-[12.5px] font-semibold uppercase tracking-[0.16em] text-ink-3">
          {title}
        </h2>
      )}
      <dl>{children}</dl>
    </section>
  );
}

/** One model group as a card on this sheet: the model's own rows, then what the two stat
 * SHEETS append to that group (Antimatter, Issue 50 — sheetRows is that one declaration, so
 * this card and the comparison's rail cannot append different things), then the group's own
 * lines where it is a list-valued one, then what THIS page appends to it.
 *
 * Order, labels, cells and whether the heading prints are all the model's; this component
 * only decides that a group is a bordered card of `<dt>/<dd>` pairs. */
function GroupCard({
  group,
  unit,
  frame,
  bare = false,
  joined = false,
}: {
  group: StatGroupSpec;
  unit: Unit;
  frame: StatFrame;
  bare?: boolean;
  /** Passed through to StatGroup: a bare section whose first row is a PEER of the rows above
   * it draws the rule at the join. True for the second cloak group, whose row stands beside
   * the first's rather than continuing it; false for the Provides lines, which continue the
   * pool rows the way the game prints them. */
  joined?: boolean;
}) {
  return (
    <StatGroup
      title={group.title}
      showHeading={sheetShowsHeading(group)}
      bare={bare}
      joined={joined}
    >
      {sheetRows(group).map((spec) => (
        <ModelRow key={spec.id} spec={spec} unit={unit} frame={frame} />
      ))}
      {lineRows(group, [{ unit, frame }]).map((row) => (
        <ProvidesRow key={row.id} row={row} unit={unit} frame={frame} />
      ))}
      {APPENDED_ROWS[group.title]?.(unit, frame)}
    </StatGroup>
  );
}

/** The Cost card's bottom rows: direct price plus the fabrication price of every exotic —
 * the number a player actually pays when the exotics come off their own factory line. Reads
 * the frame's own Price cells, so with a Loadout equipped the total is the hull plus what it
 * carries (Issue 47) and matches what DPS / cost and Cost / HP divided by. */
function ExoticsFabricatedTotals({
  unit,
  frame,
  exotics,
}: {
  unit: Unit;
  frame: StatFrame;
  /** The frame's exotic price, as the rows above already read it (frameExoticPrice). */
  exotics: readonly ExoticPrice[];
}) {
  if (exotics.length === 0) return null;
  const price = {
    Credits: frameCell(unit, frame, "credits"),
    Metal: frameCell(unit, frame, "metal"),
    Crystal: frameCell(unit, frame, "crystal"),
  };
  const fabrication = exoticFabricationTotal(exotics);
  const rows: [string, number][] = [];
  for (const [label, cell] of Object.entries(price)) {
    if (cell.state !== "computed") return null;
    rows.push([label, cell.value + fabrication[label.toLowerCase() as "credits"]]);
  }
  return (
    <>
      <div className="mt-1.5 border-t border-line pt-1.5">
        <span
          className="cursor-help font-mono text-[10.5px] uppercase tracking-[0.08em] text-muted-2 underline decoration-muted decoration-dotted underline-offset-2"
          title="The direct price plus the exotic factory's price for building this unit's exotics."
        >
          With exotics fabricated
        </span>
      </div>
      {rows.map(([label, value]) => (
        <SheetRow key={label} dense label={label} value={exactNumber(value)} />
      ))}
    </>
  );
}

/** The exotic components this unit and its Loadout cost, one row each — the Cost card's own
 * addition to the shared model (a name-and-count pair, not a stat cell). */
function ExoticRows({ unit, frame }: { unit: Unit; frame: StatFrame }) {
  // Read once, for the rows and for the fabricated totals beneath them (review 2026-09-08:
  // the two had each asked the fold for the same unit under the same frame).
  const exotics = frameExoticPrice(unit, frame);
  return (
    <>
      {exotics.map((exotic) => {
        const info = exoticsById.get(exotic.exoticType);
        return (
          <SheetRow
            key={exotic.exoticType}
            center
            title={exoticComponentNote(exotic)}
            label={
              <span className="flex items-center gap-1.5">
                {info && <ArtImage asset={info.smallIcon} alt="" className="h-4 w-4" />}
                {exoticName(exotic.exoticType)}
              </span>
            }
            value={exotic.count}
          />
        );
      })}
      <ExoticsFabricatedTotals unit={unit} frame={frame} exotics={exotics} />
    </>
  );
}

function VirtualSupplyRow({ unit }: { unit: Unit }) {
  if (unit.virtualSupplyCost === undefined) return null;
  return (
    <SheetRow
      label="Virtual supply"
      title={VIRTUAL_SUPPLY_NOTE}
      value={exactNumber(unit.virtualSupplyCost)}
    />
  );
}

function StrikecraftGroup({
  unit,
  frame,
  compare,
  bare = false,
}: {
  unit: Unit;
  frame: StatFrame;
  compare: string[];
  /** Shares the Combat card since 2026-09-07 (user: "Strikecraft feels like it can be on the
   * Combat card"), the way the Provides lines share Survivability's. What a carrier fields is
   * part of what it brings to a fight, and as its own card it was a two-row island in the
   * grid. The heading goes with the chrome: "Squadrons" names the row it stands over, and the
   * craft rows beneath read as that row's contents. The section keeps its accessible name. */
  bare?: boolean;
}) {
  const factionLink = useFactionLink();
  // The hangar follows the frame since Issue 48: a hangar Ship Item grows the shared squadron
  // capacity, so an Argonev carrying its three Auxiliary Hangars reads 14 here, not 0.
  const squadrons = frameSquadrons(unit, frame);
  if (!squadrons) return null;
  return (
    <StatGroup title="Strikecraft" showHeading={!bare} bare={bare} joined={bare}>
      <SheetRow label="Squadrons" title={SQUADRONS_NOTE} value={squadrons.capacity} />
      {squadrons.capacity > 0 &&
        squadrons.options.map((option) => (
          <SheetRow
            key={option.kind}
            center
            label={
              <Link
                to="/unit/$unitId"
                params={{ unitId: option.unitId }}
                // Frame and compare set follow the link, like every unit-level link.
                search={unitLinkSearch(frame, factionLink, compare)}
                className="text-ink-3 underline decoration-line-strong underline-offset-2 hover:text-link"
                title={[
                  `${option.squadronSize} per squadron.`,
                  // Per-kind cap (2.0.2.0): the craft can fill fewer squadrons than the
                  // shared capacity — maxCraft above already honours it.
                  option.maxSquadrons !== undefined
                    ? `At most ${option.maxSquadrons} ${option.maxSquadrons === 1 ? "squadron" : "squadrons"} of this kind.`
                    : "",
                  `Opens the ${option.name} page.`,
                ]
                  .filter(Boolean)
                  .join(" ")}
              >
                {option.name}
              </Link>
            }
            value={craftCountText(option)}
          />
        ))}
    </StatGroup>
  );
}

/** This page's cards ARE the shared model's groups (ADR 0005 Game Order) for the unit's own
 * kind (ADR 0008: a structure's sheet is the structures rows, so the page and the Structures
 * list agree). Exported so the model's test can prove the sheet never restates the order it
 * is supposed to follow, nor which rows a kind gets. */
export function unitPageGroups(unit: Pick<Unit, "kind">): readonly StatGroupSpec[] {
  return statModelFor([viewKindOf(unit)]);
}

/** What this page adds to a model group, keyed by the group it attaches to: rows appended
 * inside a card, and whole cards that follow one. Keyed rather than inlined so an addition
 * can never reorder the model's rows. */
export const APPENDED_ROWS: Record<string, (unit: Unit, frame: StatFrame) => ReactNode> = {
  // Both of these used to attach to a group of their own — the exotics to Price, the virtual
  // supply to Fleet — and the two merged into Cost on 2026-09-07. An append lands after every
  // row the model declares, so the pair is ordered here: the virtual supply first, being the
  // supply-family row, and the exotics last because that block closes with its own bordered
  // "With exotics fabricated" total, which reads as the card's footer and must not have a
  // stray row beneath it. (Every unit that carries both is a structure, which has no Supply
  // row at all, so nothing is separated from a row it belongs beside.)
  Cost: (unit, frame) => (
    <>
      <VirtualSupplyRow unit={unit} />
      <ExoticRows unit={unit} frame={frame} />
    </>
  ),
};

function UnknownUnit({ unitId }: { unitId: string }) {
  useDocumentTitle("Unknown unit");
  return (
    <div className="flex flex-col items-center gap-3 py-16 text-center">
      <p className="text-[15px] text-ink-3">
        No unit with id <span className="font-mono text-ink-2">{unitId}</span> in this dataset (game{" "}
        {dataset.meta.gameVersion}).
      </p>
      {/* An unknown id has no kind to send the reader back to, so the default view it is. */}
      <Link to={SHIPS_VIEW.path} className="text-[14px] text-link hover:text-link-hover">
        Back to {SHIPS_VIEW.navLabel}
      </Link>
    </div>
  );
}

/** The entity page's comparison toggle — the prototype's bordered "Add to comparison" /
 * "In comparison" button beside the states legend. */
function CompareButton({
  unit,
  compare,
  onChange,
}: {
  unit: Unit;
  compare: string[];
  onChange: (next: string[]) => void;
}) {
  const { inSet, full, fullTitle, commit } = compareToggle(compare, unit.id, onChange);
  return (
    // aria-disabled instead of disabled: a full-set button stays focusable so its title
    // can still explain why it refuses (review round, mouse-only-explanation finding).
    <button
      type="button"
      aria-pressed={inSet}
      aria-disabled={full}
      title={inSet ? "Remove this unit from the comparison" : full ? fullTitle : undefined}
      onClick={commit}
      className={clsx(
        "rounded border px-2.5 py-1 font-mono text-[11px] uppercase tracking-[0.06em] transition-colors",
        inSet ? "cursor-pointer border-line-strong bg-raised text-ink" : "border-line text-ink-3",
        !inSet && !full && "cursor-pointer hover:border-line-strong hover:text-ink-2",
        full && "cursor-not-allowed opacity-50",
      )}
    >
      {inSet ? "✓ In comparison" : "Add to comparison"}
    </button>
  );
}

function UnitSheet({
  unit,
  frame,
  onFrameChange,
  compare,
  onCompareChange,
}: {
  unit: Unit;
  frame: StatFrame;
  onFrameChange: (next: StatFrame) => void;
  compare: string[];
  onCompareChange: (next: string[]) => void;
}) {
  useDocumentTitle(unit.name);
  const factionLink = useFactionLink();
  const groups = unitPageGroups(unit);
  // The groups that state LINES rather than stats — Provides today. They are not cards in the
  // grid: the sheet draws them inside the Survivability cell, continuing its pool rows the
  // way the game's own tooltip does (sheetShowsHeading suppresses their heading for the same
  // reason, through this same predicate). Read off the model rather than named, so a second
  // list-valued group would land beside the first instead of silently vanishing.
  const lineGroups = groups.filter(isLineGroup);
  // The two cloak groups close the sheet (user, 2026-09-07: "Cloak and Cloak detection card
  // can come last"), so the Corruption card lands directly BEFORE the first of them rather
  // than after every group. Found by the game's own words rather than by index: the titles
  // are `dataset.cloakLabels`, resolved at extraction, so a patch that renames either one
  // moves the card with it instead of quietly dropping it to the end.
  //
  // They are also ONE card (user, 2026-09-08: "I want to combine the cloak and cloak
  // detection cards") — the model still declares two groups in Game Order and this page still
  // draws them in it, but inside a single bordered box, the same shape Survivability and
  // Combat already use for the sections that continue them. Two cards was the shape the sheet
  // took when each group earned its own: on a ship the grid put Cloak Detection beside the
  // tall Corruption card and stretched it to ~680px of empty surface, then wrapped Cloaking
  // to a row of its own. Combined, a ship's sheet is an even 3x2 and the stat and the stat
  // that defeats it are read together, which is the reason ADR 0013's amendment made them
  // adjacent in the first place.
  const cloakTitles = new Set([dataset.cloakLabels.cloaking, dataset.cloakLabels.detection]);
  const cloakGroups = groups.filter((group) => cloakTitles.has(group.title));
  const firstCloakGroup = cloakGroups[0];
  // What the last change to a per-hull lever did, said out loud (ADR 0012, Issue 46): the
  // rungs a tick brought, the item an exclusion took off, the research a tick joined, the
  // refusal at the cap. Transient like the panel itself — it describes an action, not a link.
  const [changeNote, setChangeNote] = useState<string>();
  // What the Ship Items panel repeats in its own header — the Loadout's half only, never an
  // Ability Points trim the Level control caused (lib/frame-reconcile.ts, FrameTransition).
  const [itemsPanelNote, setItemsPanelNote] = useState<string>();
  const [itemsOpen, setItemsOpen] = useState(false);
  // A Ship Items action speaks for the Loadout and nothing else, so both lines say its note.
  const apply = (change: LoadoutTransition) => {
    setChangeNote(change.note);
    setItemsPanelNote(change.note);
    onFrameChange(change.frame);
  };
  const applyFrame = (change: FrameTransition) => {
    setChangeNote(change.note);
    setItemsPanelNote(change.loadoutNote);
    onFrameChange(change.frame);
  };
  // EVERY control on this page writes its frame through here, which is what makes the two
  // axes agree however the reader reaches them: unticking research the Loadout depends on
  // takes those items off and names them, and a hull whose slot count fell is refitted.
  const changeFrame = (next: StatFrame) => applyFrame(reconcileFrame(unit, frame, next));
  // Back to the view this unit is listed in: Ships for a hull, Structures for a building.
  const view = viewOf(unit);
  return (
    <article className="mx-auto flex w-full max-w-[900px] flex-col gap-5">
      <nav>
        <Link
          to={view.path}
          // The active frame and compare set follow the link back so the list matches.
          search={unitLinkSearch(frame, factionLink, compare)}
          className="font-mono text-[12px] uppercase tracking-[0.08em] text-ink-3 hover:text-ink-2"
        >
          ← {view.navLabel}
        </Link>
      </nav>

      <header className="flex flex-wrap items-center gap-x-4 gap-y-3">
        <ArtImage asset={unit.art.hudIcon} alt="" className="h-10 w-auto shrink-0" />
        <div className="flex flex-col gap-1.5">
          <h1 className="text-[24px] font-semibold leading-tight tracking-tight">{unit.name}</h1>
          <UnitFacetChips unit={unit} />
        </div>
      </header>

      {unit.description && (
        // pre-line: the game's own line breaks (factory paragraph splits, the Eidolon
        // feature-list blurbs) survive extraction as real newlines.
        <p className="max-w-[72ch] whitespace-pre-line text-[15px] text-ink-2">
          {unit.description}
        </p>
      )}

      {unit.art.tooltipPicture ? (
        <ArtImage
          asset={unit.art.tooltipPicture}
          alt={`${unit.name} tooltip picture`}
          className="h-auto w-full max-w-[459px] rounded-lg border border-line"
        />
      ) : (
        <div className="flex h-[140px] w-full max-w-[459px] items-center justify-center rounded-lg border border-line bg-surface px-6 text-center text-[13px] text-muted-2">
          The game files ship no tooltip picture for this unit.
        </div>
      )}

      {/* The frame is declared once, page-level — never as a per-unit chip (user decision,
          944d984: the header declares the frame). The legend sits on the same line so the
          states are explainable right where the stat sheet starts. */}
      {/* The note sits ABOVE the controls: the Loadout panel opens downwards from its
          trigger, so a sentence here is never covered by the panel that is open while the
          reader clicks. While the panel IS open it says the LOADOUT's half in its own header,
          so exactly one of the two speaks — and the panel, which speaks as the Loadout, never
          claims an Ability Points trim the Level control caused (review 2026-09-16). */}
      {changeNote !== undefined && !itemsOpen && (
        <p className="text-[13px] text-muted-2">{changeNote}</p>
      )}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
          <TechDropdown frame={frame} races={[unit.race]} onChange={changeFrame} />
          <CorruptionDropdown frame={frame} onChange={changeFrame} />
          {/* The per-hull lever (ADR 0012), on the 39 hulls that have Item Slots and nowhere
              else: absent, never present and empty. */}
          {hasItemSlots(unit) && (
            <LoadoutDropdown
              unit={unit}
              frame={frame}
              {...(itemsPanelNote === undefined ? {} : { note: itemsPanelNote })}
              open={itemsOpen}
              onOpenChange={setItemsOpen}
              onEquip={(id) => apply(equipOnUnit(unit, frame, id))}
              onUnequip={(id) => apply(unequipOnUnit(unit, frame, id))}
              onClearAll={() => apply(unequipAllOnUnit(frame))}
            />
          )}
          {/* The second per-hull lever (ADR 0012, Issue 50), on the 33 hulls that have an
              experience ladder and nowhere else: absent, never present and stuck at 1. */}
          {unit.leveled && <LevelDropdown frame={frame} onChange={changeFrame} />}
          {/* The third per-hull lever (ADR 0015, Issue 62), on the same 33 hulls the Level
              control is on and nowhere else: the points a hull has are a function of its
              Level, so a hull with no ladder has no pool to spend. */}
          {unit.leveled && (
            <AbilityPointsDropdown unit={unit} frame={frame} onChange={changeFrame} />
          )}
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <CompareButton unit={unit} compare={compare} onChange={onCompareChange} />
          {/* Right-aligned so the popover's right-anchored panel stays inside the
              viewport at every width. */}
          <StatesLegend />
        </div>
      </div>
      {/* Game Order (ADR 0005), straight off the stat model for this unit's kind:
          Survivability is the damage pipeline outside-in, our Efficiency layer follows the
          Combat rows it derives from, and Cost holds everything a hull costs to field. The
          sheet renders the model — it does not restate it, which is how the roster and the
          comparison drifted away from it. */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {groups.map((group) => {
          // A list-valued group (Provides) is not a card of its own on this sheet: it is
          // drawn inside the Survivability cell below, unheaded, as the continuation of the
          // pool rows the game prints it straight after (sheetShowsHeading, 2026-09-07).
          if (lineGroups.includes(group)) return null;
          // Nor is the second cloak group a card of its own: the two share one, drawn whole
          // at the first of them below.
          if (cloakGroups.includes(group) && group !== firstCloakGroup) return null;
          return (
            <Fragment key={group.title}>
              {/* The Corruption card (Issue 20) comes after every stat group but the two
                  cloak ones, which the user put last (2026-09-07). */}
              {group === firstCloakGroup && unit.corruption !== undefined && (
                <CorruptionCard unit={unit} frame={frame} />
              )}
              {group.title === "Survivability" ? (
                // ONE card holding both: the pool rows, then the unheaded Provides lines
                // running straight on from them, which is how the game's own tooltip prints
                // them (a Lirtestra Warfare Lab reads Durability → Armor Points → Hull
                // Points → Warfare Research Points → Cloak Detection, with nothing between).
                // The chrome is the card's and the sections inside are bare, so each keeps
                // its own accessible name while the reader sees one continuous list.
                <div className="rounded-lg border border-line bg-surface px-4 py-3">
                  <GroupCard bare group={group} unit={unit} frame={frame} />
                  {/* With nothing to state it renders nothing at all — a bare block over no
                      lines would read as a gap where the honest reading is that the unit
                      file says nothing the vocabulary composes. */}
                  {lineGroups.map((lineGroup) =>
                    lineRows(lineGroup, [{ unit, frame }]).length === 0 ? null : (
                      <GroupCard
                        bare
                        key={lineGroup.title}
                        group={lineGroup}
                        unit={unit}
                        frame={frame}
                      />
                    ),
                  )}
                </div>
              ) : group.title === "Combat" ? (
                // ONE card holding both, the same shape as Survivability above: the Combat
                // rows, then the squadrons a carrier fields running straight on from them
                // (user, 2026-09-07). A hull with no hangar renders nothing extra — the
                // strikecraft section returns null — so the card is unchanged for 152 units.
                <div className="rounded-lg border border-line bg-surface px-4 py-3">
                  <GroupCard bare group={group} unit={unit} frame={frame} />
                  <StrikecraftGroup bare unit={unit} frame={frame} compare={compare} />
                </div>
              ) : group === firstCloakGroup ? (
                // ONE card holding both cloak groups (user, 2026-09-08), the same shape
                // Survivability and Combat use: the chrome is the card's and each group is a
                // bare section inside it, so both keep their own accessible name while the
                // reader sees one block. Neither prints a heading — each is a single row
                // already carrying the group's word (sheetShowsHeading) — so the card reads
                // as two named rows, Cloak Detection then Cloaking, in the model's order.
                // `joined` on every section after the first: these rows are peers, not the
                // continuation the Provides lines are, so the rule at the join stays.
                <div className="rounded-lg border border-line bg-surface px-4 py-3">
                  {cloakGroups.map((cloakGroup, index) => (
                    <GroupCard
                      bare
                      joined={index > 0}
                      key={cloakGroup.title}
                      group={cloakGroup}
                      unit={unit}
                      frame={frame}
                    />
                  ))}
                </div>
              ) : (
                <GroupCard group={group} unit={unit} frame={frame} />
              )}
            </Fragment>
          );
        })}
        {/* The fallback: a sheet with no cloak group at all has nowhere to render the card
            before, so it closes with it. No view produces this today — Cloak Detection is
            both kinds — but the card must not vanish if one ever does. */}
        {firstCloakGroup === undefined && unit.corruption !== undefined && (
          <CorruptionCard unit={unit} frame={frame} />
        )}
      </div>

      <Card title="Weapons">
        <div className="overflow-x-auto px-6 py-4">
          <WeaponTable unit={unit} frame={frame} />
        </div>
      </Card>

      {unit.abilities.length > 0 && (
        <Card title="Abilities">
          <ul className="flex flex-col divide-y divide-line-faint px-6 py-1">
            {unit.abilities.map((ability) => {
              // Where the reader's selection leaves this ability — its Level (Issue 51) or,
              // on a research-levelled one, its Tech Set (Issue 54). A locked one is marked
              // and dimmed, never dropped: the list stays the complete reference it has
              // always been, the way the Corruption card renders every tier.
              const level = abilityLevel(ability, frame, unit);
              // Dimmed for either reason the ship does not have the ability: the game does
              // not offer it yet, or the reader has bought no Ability Level of it. Undimmed
              // would read as an ability the ship has, which is the overstatement ADR 0015
              // removed. The badge beside the name says which of the two it is.
              const locked =
                level.state === "locked" ||
                level.state === "research_locked" ||
                level.state === "unspent";
              return (
                <li key={ability.id} className="flex items-start gap-4 py-3">
                  <ArtImage
                    asset={ability.icon}
                    alt=""
                    className={clsx(
                      "h-8 w-auto shrink-0 rounded border border-line-faint",
                      locked && "opacity-45",
                    )}
                  />
                  <div className="flex flex-col gap-0.5">
                    <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
                      <span
                        className={clsx(
                          "text-[14.5px] font-medium",
                          locked ? "text-ink-3" : "text-ink",
                        )}
                      >
                        {ability.name}
                      </span>
                      <AbilityLevelMark level={level} levelCount={ability.levelCount} />
                    </span>
                    {ability.description && (
                      <span className="max-w-[64ch] whitespace-pre-line text-[13.5px] text-ink-3">
                        {ability.description}
                      </span>
                    )}
                    <AbilityEffects ability={ability} level={level} />
                  </div>
                </li>
              );
            })}
          </ul>
        </Card>
      )}

      <UnitItemsSection unit={unit} />
    </article>
  );
}

export function UnitPage() {
  const { unitId } = route.useParams();
  const search = route.useSearch();
  const navigate = route.useNavigate();
  const unit = unitsById.get(unitId);
  // A `?items=` link has never been through a tick, and every rule that makes a Loadout one
  // the game could field lives in the tick (issue 45's cold-parse gap, found reviewing the
  // Cloak round 2026-09-07). Settling it here is what stops a cold link rendering the
  // Anathema at Perfect (5) with the research that buys its crest unticked.
  const parsed = parseFrame(search);
  const settled = unit === undefined ? undefined : settleLoadout(unit, parsed);
  const frame = settled?.frame ?? parsed;
  const requestedCompare = parseCompareParam(search.compare);
  const compare = clampCompare(requestedCompare);
  function onFrameChange(next: StatFrame) {
    // `?items=` is written HERE and nowhere else on the site: serializeFrame deliberately
    // leaves it out, because a Loadout rides no unit-level link (ADR 0012).
    void navigate({
      search: (prev) => ({
        ...prev,
        ...serializeFrame(next),
        ...serializeLoadoutSearch(next),
        // `?level=` is written HERE too, and nowhere else: a Level rides no unit-level link
        // (ADR 0012), so serializeFrame leaves it out exactly as it leaves `?items=` out.
        ...serializeLevelSearch(next),
        // `?points=` too: an allocation is bought on one hull and rides no link (ADR 0015).
        ...serializePointsSearch(next),
      }),
    });
  }
  function onCompareChange(next: string[]) {
    void navigate({ search: (prev) => ({ ...prev, compare: serializeCompareParam(next) }) });
  }
  return (
    <>
      <CorruptionNotice value={search.corruption} />
      <TechNotice search={search} />
      <LoadoutNotice unit={unit} search={search} />
      <LoadoutSettledNotice notes={settled?.note === undefined ? [] : [settled.note]} />
      <LevelNotice unit={unit} search={search} />
      <AbilityPointsNotice unit={unit} search={search} />
      <CompareParamNotice value={search.compare} requested={requestedCompare} reportOverflow />
      {unit !== undefined && <CorruptionPoolNotice unit={unit} frame={frame} />}
      {unit === undefined ? (
        <UnknownUnit unitId={unitId} />
      ) : (
        <UnitSheet
          unit={unit}
          frame={frame}
          onFrameChange={onFrameChange}
          compare={compare}
          onCompareChange={onCompareChange}
        />
      )}
      <CompareTray
        compare={compare}
        frame={frame}
        faction={factionLinkSearch(search)}
        onChange={onCompareChange}
      />
    </>
  );
}
